import {
  ElementType,
  MouseEvent as ReactMouseEvent,
  ReactElement,
  ReactNode,
  useMemo,
  useState,
} from 'react';
import { Box, Flex } from '@chakra-ui/react';
import moment from 'moment';

import FigmaSearchIcon from 'components/Icons/FigmaSearchIcon';
import { accent, font, neutral, radius, shadow, text } from 'styles/revampTokens';

import {
  BlockNavigationDropdownItem,
  BlockNavigationClickOptions,
} from './BlockNavigationDropdown';

const DropdownBox = Box as ElementType;
const DropdownFlex = Flex as ElementType;

interface DashboardNavigationDropdownProperties {
  pages: BlockNavigationDropdownItem[];
  currentPageId?: string | number;
  allInstruments?: any[];
  onPageClick: (page: BlockNavigationDropdownItem, options?: BlockNavigationClickOptions) => void;
  onCreateClick?: () => void;
  onViewAllClick?: () => void;
  viewAllHref?: string | null;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const AVATAR_TINTS = [
  { bg: '#F1ECFE', fg: '#7C4EE0' },
  { bg: '#E7F0FF', fg: '#3B74E8' },
  { bg: '#E3F6F0', fg: '#0E9E7E' },
  { bg: '#FFF2E0', fg: '#D9850B' },
  { bg: '#FDEBF1', fg: '#D14D86' },
  { bg: '#ECEEFF', fg: '#5B5FE0' },
] as const;

const RECENT_LIMIT = 4;

type TabKey = 'all' | 'recent' | 'favourites';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const hashIndex = (value: string, modulo: number): number => {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 31 + (value.codePointAt(index) ?? 0)) % 100_000;
  }
  return hash % modulo;
};

const initialsOf = (title: string): string =>
  title
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w.charAt(0))
    .join('')
    .toUpperCase() || '•';

const compactAgo = (value?: string): string | null => {
  if (!value) return null;
  const then = moment.utc(value);
  if (!then.isValid()) return null;
  const now = moment.utc();
  const mins = now.diff(then, 'minutes');
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = now.diff(then, 'hours');
  if (hours < 24) return `${hours}h ago`;
  const days = now.diff(then, 'days');
  if (days < 7) return `${days}d ago`;
  return `${now.diff(then, 'weeks')}w ago`;
};

const isFav = (source: any): boolean =>
  Boolean(source?.favourite || source?.is_favourite || source?.isFavourite);

const isModifiedClick = (event: ReactMouseEvent) =>
  event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0;

// ─── Icons ────────────────────────────────────────────────────────────────────

const ArrowIcon = () => (
  <svg width='16' height='16' viewBox='0 0 20 20' fill='none'>
    <path
      d='M11.667 5.008L10.492 6.183l2.975 2.984H3.333v1.666h10.134l-2.975 2.984 1.175 1.175L16.667 10l-5-4.992Z'
      fill='currentColor'
    />
  </svg>
);

const ChevronRightIcon = () => (
  <svg width='14' height='14' viewBox='0 0 16 16' fill='none'>
    <path
      d='M6 4l4 4-4 4'
      stroke='currentColor'
      strokeWidth='1.4'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

const PlusCircleIcon = () => (
  <svg width='16' height='16' viewBox='0 0 16 16' fill='none'>
    <circle cx='8' cy='8' r='6.5' stroke='currentColor' strokeWidth='1.2' />
    <path d='M8 5v6M5 8h6' stroke='currentColor' strokeWidth='1.3' strokeLinecap='round' />
  </svg>
);

const MetricIcon = () => (
  <svg width='14' height='14' viewBox='0 0 14 14' fill='none'>
    <rect x='1' y='7' width='3' height='6' rx='0.8' fill='currentColor' />
    <rect x='5.5' y='4' width='3' height='9' rx='0.8' fill='currentColor' />
    <rect x='10' y='1' width='3' height='12' rx='0.8' fill='currentColor' />
  </svg>
);

const ChartIcon = () => (
  <svg width='14' height='14' viewBox='0 0 14 14' fill='none'>
    <circle cx='7' cy='7' r='5.5' stroke='currentColor' strokeWidth='1.1' />
    <path d='M7 7L7 2.5' stroke='currentColor' strokeWidth='1.1' strokeLinecap='round' />
    <path d='M7 7L11 9.5' stroke='currentColor' strokeWidth='1.1' strokeLinecap='round' />
    <path d='M7 7L3.5 9.5' stroke='currentColor' strokeWidth='1.1' strokeLinecap='round' />
  </svg>
);

const TableIcon = () => (
  <svg width='14' height='14' viewBox='0 0 14 14' fill='none'>
    <rect x='1.5' y='1.5' width='11' height='11' rx='1.5' stroke='currentColor' strokeWidth='1.1' />
    <path d='M1.5 5h11M5 5v7.5' stroke='currentColor' strokeWidth='1.1' />
  </svg>
);

const VarianceIcon = () => (
  <svg width='14' height='14' viewBox='0 0 14 14' fill='none'>
    <path
      d='M1.5 10L5 6l3 3 4-5'
      stroke='currentColor'
      strokeWidth='1.2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path
      d='M10 4h2.5v2.5'
      stroke='currentColor'
      strokeWidth='1.2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

const ClockIcon = () => (
  <svg width='13' height='13' viewBox='0 0 14 14' fill='none'>
    <circle cx='7' cy='7' r='5.6' stroke='currentColor' strokeWidth='1.1' />
    <path d='M7 4V7L9 8.2' stroke='currentColor' strokeWidth='1.1' strokeLinecap='round' />
  </svg>
);

// ─── Group builder ────────────────────────────────────────────────────────────

interface PageGroup {
  key: string;
  label: string;
  items: BlockNavigationDropdownItem[];
}

const buildGroups = (pages: BlockNavigationDropdownItem[], tab: TabKey): PageGroup[] => {
  if (tab === 'recent') {
    const sorted = [...pages]
      .sort((a, b) =>
        String(b.source?.updated_at ?? b.source?.last_modified_at ?? '').localeCompare(
          String(a.source?.updated_at ?? a.source?.last_modified_at ?? ''),
        ),
      )
      .slice(0, RECENT_LIMIT);
    return [{ key: 'recent', label: 'Recent', items: sorted }];
  }

  if (tab === 'favourites') {
    return [{ key: 'fav', label: 'Favourites', items: pages.filter((p) => isFav(p.source)) }];
  }

  // Group by category field if present
  const categoryMap = new Map<string, BlockNavigationDropdownItem[]>();
  const uncategorised: BlockNavigationDropdownItem[] = [];

  pages.forEach((page) => {
    const cat: string | undefined =
      page.source?.category ?? page.source?.group ?? page.source?.page_category;
    if (cat) {
      if (!categoryMap.has(cat)) categoryMap.set(cat, []);
      categoryMap.get(cat)!.push(page);
    } else {
      uncategorised.push(page);
    }
  });

  const hasCategories = categoryMap.size > 0;
  const groups: PageGroup[] = [];

  if (hasCategories) {
    // Categories exist: show Recent + category groups
    const sorted = [...pages].sort((a, b) =>
      String(b.source?.updated_at ?? b.source?.last_modified_at ?? '').localeCompare(
        String(a.source?.updated_at ?? a.source?.last_modified_at ?? ''),
      ),
    );
    const recent = sorted.slice(0, RECENT_LIMIT);
    if (recent.length > 0) {
      groups.push({ key: '__recent', label: 'Recent', items: recent });
    }
    categoryMap.forEach((items, cat) => {
      groups.push({ key: cat, label: cat.toUpperCase(), items });
    });
    if (uncategorised.length > 0) {
      groups.push({ key: '__other', label: 'Other', items: uncategorised });
    }
  } else {
    // No categories — just a flat list, no duplication
    groups.push({ key: '__all', label: 'All pages', items: pages });
  }

  return groups;
};

// ─── Component ────────────────────────────────────────────────────────────────

const DashboardNavigationDropdown = ({
  pages,
  currentPageId,
  allInstruments,
  onPageClick,
  onCreateClick,
  onViewAllClick,
  viewAllHref,
}: DashboardNavigationDropdownProperties): ReactElement => {
  const [query, setQuery] = useState('');
  const tab: TabKey = 'all';
  const [activeId, setActiveId] = useState<string | number | null>(currentPageId ?? null);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return needle ? pages.filter((p) => p.title.toLowerCase().includes(needle)) : pages;
  }, [pages, query]);

  const groups = useMemo(() => buildGroups(filtered, tab), [filtered, tab]);

  const allItems = useMemo(() => groups.flatMap((g) => g.items), [groups]);
  const activePage = allItems.find((p) => p.id === activeId) ?? allItems[0] ?? null;
  const activeTint = activePage
    ? AVATAR_TINTS[hashIndex(activePage.title, AVATAR_TINTS.length)]
    : AVATAR_TINTS[0];

  const renderRow = (page: BlockNavigationDropdownItem): ReactNode => {
    const tint = AVATAR_TINTS[hashIndex(page.title, AVATAR_TINTS.length)];
    const isActive = activePage?.id === page.id;
    const category: string = page.source?.category ?? page.source?.group ?? '';

    return (
      <DropdownFlex
        key={page.id}
        alignItems='center'
        gap='10px'
        px='10px'
        py='7px'
        cursor='pointer'
        borderRadius={radius.control}
        bg={isActive ? accent.subtle : 'transparent'}
        transition='background 0.1s ease'
        _hover={{ bg: isActive ? accent.subtle : neutral.surfaceHover }}
        onMouseEnter={() => setActiveId(page.id)}
        onClick={(event: ReactMouseEvent) => {
          if (isModifiedClick(event)) return;
          event.stopPropagation();
          onPageClick(page);
        }}
        onContextMenu={(event: ReactMouseEvent) => {
          event.preventDefault();
          event.stopPropagation();
          onPageClick(page, { openInNewTab: true });
        }}
      >
        {/* Icon avatar */}
        <DropdownFlex
          w='30px'
          h='30px'
          flexShrink={0}
          alignItems='center'
          justifyContent='center'
          borderRadius={radius.control}
          bg={tint.bg}
          color={tint.fg}
          fontFamily={font.body}
          fontSize='11px'
          fontWeight='700'
        >
          {initialsOf(page.title)}
        </DropdownFlex>

        {/* Text */}
        <DropdownFlex direction='column' flex='1 1 0' minW={0} gap='1px'>
          <DropdownBox
            color={isActive ? accent.solid : text.primary}
            fontFamily={font.body}
            fontSize='13.5px'
            fontWeight={isActive ? '600' : '500'}
            lineHeight='18px'
            noOfLines={1}
          >
            {page.title}
          </DropdownBox>
          {category && (
            <DropdownBox color={text.secondary} fontSize='11.5px' lineHeight='15px' noOfLines={1}>
              {category}
            </DropdownBox>
          )}
        </DropdownFlex>

        {/* Right: chevron */}
        <DropdownBox color={isActive ? accent.solid : text.muted} flexShrink={0}>
          <ChevronRightIcon />
        </DropdownBox>
      </DropdownFlex>
    );
  };

  return (
    <DropdownFlex
      w='520px'
      maxW='96vw'
      h='412px'
      maxH='calc(100vh - 96px)'
      bg={neutral.surface}
      border={`1px solid ${neutral.border}`}
      boxShadow={shadow.overlay}
      borderRadius={radius.overlay}
      overflow='hidden'
      flexDirection='column'
      onClick={(event: ReactMouseEvent) => event.stopPropagation()}
    >
      {/* ── Search ── */}
      <DropdownFlex
        alignItems='center'
        gap='8px'
        px='14px'
        h='46px'
        flexShrink={0}
        borderBottom={`1px solid ${neutral.border}`}
      >
        <FigmaSearchIcon width={13} height={13} color={text.muted} />
        <DropdownBox
          as='input'
          type='text'
          value={query}
          placeholder='Search pages...'
          onChange={(event: any) => setQuery(event.target.value)}
          flex='1'
          minW={0}
          bg='transparent'
          border='none'
          outline='none'
          color={text.primary}
          fontFamily={font.body}
          fontSize='13px'
          sx={{ '::placeholder': { color: text.muted } }}
        />
      </DropdownFlex>

      {/* ── Body ── */}
      <DropdownFlex flex='1 1 auto' minH={0}>
        {/* Left: grouped list */}
        <DropdownFlex direction='column' w='220px' flexShrink={0} overflowY='auto' p='6px'>
          {allItems.length === 0 ? (
            <DropdownBox
              px='12px'
              py='32px'
              textAlign='center'
              color={text.secondary}
              fontSize='13px'
              fontFamily={font.body}
            >
              {query.trim() ? 'No dashboards match your search' : 'No dashboards yet'}
            </DropdownBox>
          ) : (
            groups.map((group) => (
              <DropdownFlex key={group.key} direction='column' mb='4px'>
                <DropdownBox
                  px='12px'
                  pt='10px'
                  pb='5px'
                  color={text.muted}
                  fontFamily={font.body}
                  fontSize='10.5px'
                  fontWeight='700'
                  letterSpacing='0.07em'
                  textTransform='uppercase'
                >
                  {group.label}
                </DropdownBox>
                {group.items.map((page) => renderRow(page))}
              </DropdownFlex>
            ))
          )}
        </DropdownFlex>

        {/* Right: detail pane */}
        <DropdownFlex
          direction='column'
          flex='1 1 0'
          minW={0}
          borderLeft={`1px solid ${neutral.border}`}
          bg={neutral.surfaceMuted}
        >
          {activePage ? (
            <DropdownFlex direction='column' p='14px' gap='10px'>
              {/* Avatar + name row */}
              <DropdownFlex alignItems='center' gap='10px'>
                <DropdownFlex
                  w='32px'
                  h='32px'
                  flexShrink={0}
                  alignItems='center'
                  justifyContent='center'
                  borderRadius={radius.control}
                  bg={activeTint.bg}
                  color={activeTint.fg}
                  fontFamily={font.body}
                  fontSize='12px'
                  fontWeight='700'
                >
                  {initialsOf(activePage.title)}
                </DropdownFlex>
                <DropdownFlex direction='column' gap='2px' flex='1 1 0' minW={0}>
                  <DropdownBox
                    color={text.primary}
                    fontFamily={font.display}
                    fontSize='14px'
                    fontWeight='600'
                    lineHeight='18px'
                    noOfLines={1}
                  >
                    {activePage.title}
                  </DropdownBox>
                  {(activePage.source?.category ?? activePage.source?.group) && (
                    <DropdownBox color={text.secondary} fontFamily={font.body} fontSize='11px'>
                      {activePage.source?.category ?? activePage.source?.group}
                    </DropdownBox>
                  )}
                </DropdownFlex>
              </DropdownFlex>

              {/* Description */}
              {activePage.source?.description && (
                <DropdownBox
                  color={text.secondary}
                  fontFamily={font.body}
                  fontSize='12px'
                  lineHeight='17px'
                  noOfLines={3}
                >
                  {activePage.source.description}
                </DropdownBox>
              )}

              {/* Widget stats grid */}
              {(() => {
                const source = activePage.source ?? {};
                const pageInstruments = Array.isArray(allInstruments)
                  ? allInstruments.filter(
                      (inst: any) => String(inst?.page_id) === String(activePage.id),
                    )
                  : [];
                const countByType = (types: string[]) =>
                  pageInstruments.length > 0
                    ? pageInstruments.filter((inst: any) => types.includes(inst?.instrument_type))
                        .length
                    : null;
                const stats = [
                  {
                    icon: <MetricIcon />,
                    count:
                      source.kpis_count ??
                      source.metrics_count ??
                      source.kpi_count ??
                      countByType(['kpi']) ??
                      0,
                    label: 'KPIs',
                  },
                  {
                    icon: <ChartIcon />,
                    count:
                      source.charts_count ??
                      source.chart_count ??
                      countByType(['line', 'bar', 'stack', 'chart']) ??
                      0,
                    label: 'Charts',
                  },
                  {
                    icon: <TableIcon />,
                    count: source.tables_count ?? source.table_count ?? countByType(['table']) ?? 0,
                    label: 'Tables',
                  },
                  {
                    icon: <VarianceIcon />,
                    count:
                      source.variance_count ??
                      source.variances_count ??
                      countByType(['variance']) ??
                      0,
                    label: 'Variance',
                  },
                ];

                const total =
                  source.widgets_count ??
                  source.total_widgets ??
                  (pageInstruments.length > 0
                    ? pageInstruments.length
                    : stats.reduce((sum, s) => sum + (s.count ?? 0), 0));

                return (
                  <DropdownFlex direction='column' gap='8px'>
                    {total != null && (
                      <DropdownFlex
                        alignItems='center'
                        justifyContent='space-between'
                        px='10px'
                        py='7px'
                        border={`1px solid ${neutral.border}`}
                        borderRadius={radius.control}
                        bg={neutral.surface}
                      >
                        <DropdownBox color={text.secondary} fontFamily={font.body} fontSize='12px'>
                          Total instruments
                        </DropdownBox>
                        <DropdownBox
                          color={text.primary}
                          fontFamily={font.body}
                          fontSize='13px'
                          fontWeight='700'
                        >
                          {total}
                        </DropdownBox>
                      </DropdownFlex>
                    )}
                    {stats.length > 0 && (
                      <DropdownFlex
                        border={`1px solid ${neutral.border}`}
                        borderRadius={radius.control}
                        bg={neutral.surface}
                        overflow='hidden'
                        flexWrap='wrap'
                      >
                        {stats.map((stat, index) => (
                          <DropdownFlex
                            key={stat.label}
                            w='50%'
                            direction='column'
                            alignItems='center'
                            py='9px'
                            gap='3px'
                            borderLeft={index % 2 !== 0 ? `1px solid ${neutral.border}` : undefined}
                            borderTop={index >= 2 ? `1px solid ${neutral.border}` : undefined}
                          >
                            <DropdownFlex alignItems='center' gap='4px'>
                              <DropdownBox color={accent.solid}>{stat.icon}</DropdownBox>
                              <DropdownBox
                                color={text.primary}
                                fontFamily={font.body}
                                fontSize='14px'
                                fontWeight='700'
                                lineHeight='1'
                              >
                                {stat.count}
                              </DropdownBox>
                            </DropdownFlex>
                            <DropdownBox color={text.muted} fontFamily={font.body} fontSize='10px'>
                              {stat.label}
                            </DropdownBox>
                          </DropdownFlex>
                        ))}
                      </DropdownFlex>
                    )}
                  </DropdownFlex>
                );
              })()}

              {/* Updated */}
              {compactAgo(activePage.source?.updated_at ?? activePage.source?.last_modified_at) && (
                <DropdownFlex alignItems='center' gap='7px' color={text.secondary}>
                  <ClockIcon />
                  <DropdownBox fontFamily={font.body} fontSize='12px'>
                    Updated{' '}
                    {compactAgo(
                      activePage.source?.updated_at ?? activePage.source?.last_modified_at,
                    )}
                  </DropdownBox>
                </DropdownFlex>
              )}

              {/* CTAs */}
              <DropdownFlex direction='column' gap='8px'>
                <DropdownFlex
                  as='button'
                  type='button'
                  alignItems='center'
                  justifyContent='center'
                  gap='6px'
                  h='38px'
                  borderRadius={radius.control}
                  bg={accent.solid}
                  color='#FFFFFF'
                  fontFamily={font.body}
                  fontSize='13px'
                  fontWeight='600'
                  cursor='pointer'
                  transition='background 0.1s ease'
                  _hover={{ bg: accent.hover }}
                  onClick={() => onPageClick(activePage)}
                >
                  Go to dashboard
                  <ArrowIcon />
                </DropdownFlex>
              </DropdownFlex>
            </DropdownFlex>
          ) : (
            <DropdownBox
              color={text.secondary}
              fontSize='13px'
              m='auto'
              textAlign='center'
              p='16px'
            >
              Hover a dashboard to preview it
            </DropdownBox>
          )}
        </DropdownFlex>
      </DropdownFlex>

      {/* ── Footer ── */}
      <DropdownFlex
        px='16px'
        h='46px'
        flexShrink={0}
        alignItems='center'
        justifyContent='space-between'
        borderTop={`1px solid ${neutral.border}`}
        bg={neutral.surfaceMuted}
      >
        <DropdownFlex
          as='button'
          type='button'
          alignItems='center'
          gap='7px'
          color={accent.solid}
          fontFamily={font.body}
          fontSize='12.5px'
          fontWeight='600'
          cursor='pointer'
          _hover={{ opacity: 0.8 }}
          onClick={onCreateClick}
        >
          <PlusCircleIcon />
          Create new page
        </DropdownFlex>

        <DropdownFlex
          as={viewAllHref ? 'a' : 'button'}
          href={viewAllHref ?? undefined}
          type={viewAllHref ? undefined : 'button'}
          alignItems='center'
          gap='5px'
          color={text.secondary}
          fontFamily={font.body}
          fontSize='12.5px'
          fontWeight='500'
          cursor='pointer'
          textDecoration='none'
          _hover={{ color: accent.solid }}
          transition='color 0.1s ease'
          onClick={viewAllHref ? undefined : onViewAllClick}
        >
          View all dashboards
          <ArrowIcon />
        </DropdownFlex>
      </DropdownFlex>
    </DropdownFlex>
  );
};

export default DashboardNavigationDropdown;
